/**
 * Greybox mecha blockout — box-stack silhouette, shown when snapshot.inMecha.
 * Spawn scale punch on transform; rocket flash + stomp bounce for climax
 * scale-inversion (horde becomes fodder under the live sim mecha).
 */
import * as THREE from "three";
import { makeMechaMaterial } from "./materials.js";
import { GREYBOX } from "./materials.js";
export function createMechaMesh() {
    const group = new THREE.Group();
    group.name = "mechaGreybox";
    group.visible = false;
    const mat = makeMechaMaterial();
    const accentMat = new THREE.MeshStandardMaterial({
        color: GREYBOX.mechaAccent,
        emissive: 0x475569,
        emissiveIntensity: 0.35,
        roughness: 0.45,
        metalness: 0.4,
        flatShading: true,
    });
    const bodyGeo = new THREE.BoxGeometry(1.6, 2.4, 1.2);
    const body = new THREE.Mesh(bodyGeo, mat);
    body.position.y = 1.4;
    body.castShadow = true;
    group.add(body);
    const headGeo = new THREE.BoxGeometry(0.9, 0.7, 0.9);
    const head = new THREE.Mesh(headGeo, mat);
    head.position.y = 2.95;
    head.castShadow = true;
    group.add(head);
    const visorGeo = new THREE.BoxGeometry(0.7, 0.22, 0.2);
    const visor = new THREE.Mesh(visorGeo, accentMat);
    visor.position.set(0, 2.95, 0.45);
    group.add(visor);
    // Arms
    const armGeo = new THREE.BoxGeometry(0.4, 1.4, 0.4);
    const leftArm = new THREE.Mesh(armGeo, mat);
    leftArm.position.set(-1.15, 1.6, 0);
    leftArm.castShadow = true;
    group.add(leftArm);
    const rightArm = new THREE.Mesh(armGeo, mat);
    rightArm.position.set(1.15, 1.6, 0);
    rightArm.castShadow = true;
    group.add(rightArm);
    // Legs
    const legGeo = new THREE.BoxGeometry(0.45, 1.1, 0.5);
    const leftLeg = new THREE.Mesh(legGeo, mat);
    leftLeg.position.set(-0.45, 0.55, 0);
    leftLeg.castShadow = true;
    group.add(leftLeg);
    const rightLeg = new THREE.Mesh(legGeo, mat);
    rightLeg.position.set(0.45, 0.55, 0);
    rightLeg.castShadow = true;
    group.add(rightLeg);
    // Shoulder rocket pods (climax juice anchors)
    const podGeo = new THREE.BoxGeometry(0.35, 0.35, 0.7);
    const leftPod = new THREE.Mesh(podGeo, accentMat);
    leftPod.position.set(-1.15, 2.2, -0.35);
    group.add(leftPod);
    const rightPod = new THREE.Mesh(podGeo, accentMat);
    rightPod.position.set(1.15, 2.2, -0.35);
    group.add(rightPod);
    const rocketGeo = new THREE.SphereGeometry(0.28, 8, 6);
    const rocketMat = new THREE.MeshBasicMaterial({
        color: 0xfbbf24,
        transparent: true,
        opacity: 0,
        depthWrite: false,
    });
    const rocketL = new THREE.Mesh(rocketGeo, rocketMat);
    rocketL.position.set(-1.15, 2.2, -0.9);
    group.add(rocketL);
    const rocketR = new THREE.Mesh(rocketGeo, rocketMat.clone());
    rocketR.position.set(1.15, 2.2, -0.9);
    group.add(rocketR);
    let rocketPulse = 0;
    let spawnBloomT = 0;
    let climaxIntensity = 0;
    let climaxTime = 0;
    let baseY = 0;
    const applyScale = () => {
        const u = Math.max(0, Math.min(1, spawnBloomT));
        // Overshoot scale then settle to 1
        const bloom = 1 + u * 0.45 * Math.sin(u * Math.PI);
        // Climax stomp: slight Y squash/stretch so scale inversion is felt
        const stomp = climaxIntensity > 0
            ? 1 + Math.sin(climaxTime * 9.5) * 0.04 * climaxIntensity
            : 1;
        group.scale.set(bloom, bloom * stomp, bloom);
        accentMat.emissiveIntensity = 0.35 + u * 1.2 + climaxIntensity * 0.45;
    };
    const setPose = (centroidX, centroidZ) => {
        const stompY = climaxIntensity > 0
            ? Math.abs(Math.sin(climaxTime * 9.5)) * 0.12 * climaxIntensity
            : 0;
        group.position.set(centroidX, baseY + stompY, centroidZ);
        // Forward lean while firing down the bridge
        group.rotation.x =
            climaxIntensity > 0 ? -0.06 * climaxIntensity : 0;
    };
    const setVisible = (visible) => {
        group.visible = visible;
    };
    const setSpawnBloom = (t) => {
        spawnBloomT = Math.max(0, Math.min(1, t));
        applyScale();
    };
    const pulseRocket = (intensity) => {
        rocketPulse = Math.max(rocketPulse, Math.max(0, Math.min(1, intensity)));
    };
    const setClimaxMotion = (timeSeconds, intensity) => {
        climaxTime = timeSeconds;
        climaxIntensity = Math.max(0, Math.min(1, intensity));
        applyScale();
    };
    // Decay rocket flash each pose update via external tick — expose via setSpawnBloom path
    const decayRockets = (dt) => {
        if (rocketPulse <= 0) {
            rocketMat.opacity = 0;
            rocketR.material.opacity = 0;
            return;
        }
        rocketPulse = Math.max(0, rocketPulse - dt * 4);
        const op = rocketPulse * 0.85;
        rocketMat.opacity = op;
        rocketR.material.opacity = op;
        const s = 0.6 + rocketPulse * 1.4;
        rocketL.scale.setScalar(s);
        rocketR.scale.setScalar(s);
    };
    // Attach decay to setPose by tracking last call time externally — wrap setPose
    let lastPoseT = 0;
    const setPoseWithDecay = (centroidX, centroidZ) => {
        setPose(centroidX, centroidZ);
        const now = typeof performance !== "undefined" ? performance.now() / 1000 : 0;
        const dt = lastPoseT > 0 ? Math.min(0.1, now - lastPoseT) : 1 / 60;
        lastPoseT = now || lastPoseT + 1 / 60;
        decayRockets(dt > 0 ? dt : 1 / 60);
    };
    const dispose = () => {
        bodyGeo.dispose();
        headGeo.dispose();
        visorGeo.dispose();
        armGeo.dispose();
        legGeo.dispose();
        podGeo.dispose();
        rocketGeo.dispose();
        mat.dispose();
        accentMat.dispose();
        rocketMat.dispose();
        rocketR.material.dispose();
        group.clear();
    };
    return {
        group,
        setPose: setPoseWithDecay,
        setVisible,
        setSpawnBloom,
        pulseRocket,
        setClimaxMotion,
        dispose,
    };
}
//# sourceMappingURL=mecha.js.map